/**
 * Designation API Service
 * Handles designation CRUD operations with backend
 */

import { apiGet, apiPost, apiPut, apiDelete } from '@/shared/api/apiClient';
import type { PagedResult } from '@/types';

/**
 * Designation DTOs matching backend models
 */
export interface DesignationDto {
  id: string;
  name: string;
  code: string | null;
  description: string | null;
  level: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isActive: boolean;
  tenantId: string;
  createdAt: string;
  updatedAt: string | null;
}

export interface DesignationListDto {
  id: string;
  name: string;
  code: string | null;
  level: string | null;
  departmentId: string | null;
  departmentName: string | null;
  isActive: boolean;
}

export interface CreateDesignationRequest {
  name: string;
  code?: string;
  description?: string;
  level?: string;
  departmentId?: string;
  isActive?: boolean;
  tenantId: string;
  userId: string;
}

export interface UpdateDesignationRequest {
  name: string;
  code?: string;
  description?: string;
  level?: string;
  departmentId?: string;
  isActive?: boolean;
  tenantId: string;
  userId: string;
}

/**
 * Get all designations for a tenant
 */
export async function getAllDesignations(tenantId: string): Promise<DesignationListDto[]> {
  return apiGet<DesignationListDto[]>(`/designations?tenantId=${tenantId}`);
}

export interface DesignationListParams {
  tenantId: string;
  /** 0-based row offset. */
  offset?: number;
  limit?: number;
  search?: string;
}

export interface DesignationListResult {
  items: DesignationListDto[];
  totalCount: number;
}

/**
 * Server-side paginated/searchable variant of `getAllDesignations`, used only by
 * the Configuration screen's table. `getAllDesignations` above is left untouched —
 * it still serves dropdown selects elsewhere unpaginated.
 */
export async function getDesignationsPaged(params: DesignationListParams): Promise<DesignationListResult> {
  const query = new URLSearchParams({ tenantId: params.tenantId });
  if (params.offset !== undefined) query.append('offset', String(params.offset));
  if (params.limit !== undefined) query.append('limit', String(params.limit));
  if (params.search) query.append('search', params.search);

  const data = await apiGet<PagedResult<DesignationListDto>>(`/designations?${query.toString()}`);
  return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
}

/**
 * Get a designation by ID
 */
export async function getDesignationById(id: string, tenantId: string): Promise<DesignationDto> {
  return apiGet<DesignationDto>(`/designations/${id}?tenantId=${tenantId}`);
}

/**
 * Create a new designation
 */
export async function createDesignation(data: CreateDesignationRequest): Promise<{ id: string }> {
  if (data.userId.length < 10) {
    data.userId = "00000000-0000-0000-0000-000000000000";
  }
  return apiPost<{ id: string }>('/designations', data);
}

/**
 * Update an existing designation
 */
export async function updateDesignation(id: string, data: UpdateDesignationRequest): Promise<void> {
  if (data.userId.length < 10) {
    data.userId = "00000000-0000-0000-0000-000000000000";
  }
  return apiPut<void>(`/designations/${id}`, data);
}

/**
 * Delete a designation
 */
export async function deleteDesignation(id: string, tenantId: string, userId: string): Promise<void> {
  let effectiveUserId = userId;
  if (userId.length < 10) {
    effectiveUserId = "00000000-0000-0000-0000-000000000000";
  }
  return apiDelete<void>(`/designations/${id}`, { 
    tenantId, 
    userId: effectiveUserId 
  });
}
